import { useEffect, useState, type ReactNode } from "react";
import { APP_ENV, APP_VERSION, auth } from "../lib/api";
import { Link, useRoute } from "../lib/router";
import { useSession } from "../lib/session";
import { IconMenu } from "./icons";
import { FoldAll } from "./ui";
import { LateBanner } from "../pages/CommandCenter";
import { GlobalSearch } from "./GlobalSearch";
import { Bell } from "./Bell";

export type Workspace = "home" | "leads" | "team" | "clients" | "settings";

const WS: { key: Workspace; label: string; color: string; module: string; to: string }[] = [
  { key: "team", label: "Team", color: "var(--sky)", module: "team", to: "/team" },
  { key: "leads", label: "Leads", color: "var(--gold-2)", module: "leads", to: "/leads" },
  { key: "clients", label: "Clients", color: "#5b78f0", module: "clients", to: "/clients" },
];

const NAV: Record<Workspace, { to: string; label: string; module?: string; exact?: boolean }[]> = {
  home: [],
  leads: [
    { to: "/leads", label: "Leads today", module: "today", exact: true },
    { to: "/leads/people", label: "People" },
    { to: "/leads/pipeline", label: "Pipelines", module: "pipelines" },
    { to: "/leads/meetings", label: "Meetings", module: "activities" },
    { to: "/leads/events", label: "Events", module: "events" },
    { to: "/leads/cleanup", label: "Clean up lists" },
    { to: "/leads/removed", label: "Removed", module: "people_admin" },
    { to: "/leads/content", label: "Content log", module: "content" },
  ],
  team: [
    { to: "/team", label: "Overview", exact: true },
    { to: "/team/roster", label: "Roster" },
    { to: "/team/tracker", label: "Recruit tracker" },
    { to: "/team/tree", label: "Team tree" },
  ],
  clients: [{ to: "/clients", label: "All clients", exact: true }, { to: "/clients/reviews", label: "Annual reviews" }, { to: "/clients/import", label: "Import solutions" }, { to: "/leads/meetings", label: "Meetings", module: "activities" }],
  settings: [
    { to: "/settings/how", label: "How it works" },
    { to: "/settings", label: "Targets & rules", module: "settings", exact: true },
    { to: "/settings/goals", label: "Goals", module: "settings" },
    { to: "/settings/milestones", label: "Milestones", module: "settings" },
    { to: "/settings/lists", label: "Lists", module: "settings" },
    { to: "/settings/pipelines", label: "Pipelines", module: "settings" },
    { to: "/settings/routing", label: "Who does what", module: "settings" },
    { to: "/settings/automations", label: "Emails & reminders", module: "settings" },
    { to: "/settings/phone-contacts", label: "Phone contacts", module: "settings" },
    { to: "/settings/users", label: "Users & permissions", module: "users" },
    { to: "/settings/profile", label: "Your profile" },
    { to: "/settings/security", label: "Security (2-step)" },
  ],
};

export function Shell({ workspace, children }: { workspace: Workspace; children: ReactNode }) {
  const { path } = useRoute();
  const { profile, can } = useSession();
  const [open, setOpen] = useState(false);
  // the menu on the left: full or slim (dots only), remembered on this device
  const [slim, setSlim] = useState(() => { try { return localStorage.getItem("rca.rail.slim") === "1"; } catch { return false; } });
  const toggleSlim = () => setSlim((x) => { try { localStorage.setItem("rca.rail.slim", x ? "0" : "1"); } catch { /* fine */ } return !x; });
  useEffect(() => setOpen(false), [path]);
  useEffect(() => { document.body.dataset.ws = workspace; }, [workspace]);

  const nav = NAV[workspace].filter((n) => !n.module || can(n.module));
  const isActive = (n: { to: string; exact?: boolean }) => (n.exact ? path === n.to : path === n.to || path.startsWith(n.to + "/"));

  return (
    <div className={slim ? "shell slim" : "shell"}>
      <div className="mobile-bar">
        <button onClick={() => setOpen(true)} aria-label="Open menu"><IconMenu /></button>
        <img src="/logo-64.png" alt="" />
        <strong>{workspace === "home" ? (path === "/tasks" ? "Tasks" : path === "/scoreboard" ? "Scoreboard" : path === "/review" ? "Weekly review" : "Command Center") : WS.find((w) => w.key === workspace)?.label ?? "Settings"}</strong>
      </div>
      <div className="topbar-right"><Bell /></div>
      {open && <div className="scrim" style={{ zIndex: 45 }} onClick={() => setOpen(false)} />}
      <nav className="rail" data-open={open} aria-label="Main">
        <Link to="/" className="brand">
          <img src="/logo.png" alt="Royal Compass Advisory" />
          <span className="bt">Royal Compass<em>Advisory CRM</em></span>
        </Link>
        <button className="rail-fold" onClick={toggleSlim} title={slim ? "Show the full menu" : "Make the menu narrow"} aria-label={slim ? "Expand the menu" : "Collapse the menu"}>{slim ? "»" : "«"}</button>
        <GlobalSearch />
        <div className="ws-switch home-links">
          <Link to="/command" aria-current={path === "/command" ? "page" : undefined} title="Command Center"><span className="dot" style={{ background: "var(--alert)" }} /><span className="rl">Command Center</span></Link>
          {can("tasks") && <Link to="/tasks" aria-current={path === "/tasks" ? "page" : undefined} title="Tasks"><span className="dot" style={{ background: "var(--warm)" }} /><span className="rl">Tasks</span></Link>}
          <Link to="/review" aria-current={path === "/review" ? "page" : undefined} title="Weekly review"><span className="dot" style={{ background: "var(--sky)" }} /><span className="rl">Weekly review</span></Link>
          {can("tasks") && <Link to="/scoreboard" aria-current={path === "/scoreboard" ? "page" : undefined} title="Scoreboard"><span className="dot" style={{ background: "var(--gold-2)" }} /><span className="rl">Scoreboard</span></Link>}
          {(can("dashboards") || can("team") || can("activities")) && <Link to="/reports" aria-current={path === "/reports" ? "page" : undefined} title="Reports"><span className="dot" style={{ background: "var(--sky)" }} /><span className="rl">Reports</span></Link>}
          <Link to="/birthdays" aria-current={path === "/birthdays" ? "page" : undefined} title="Birthdays"><span className="dot" style={{ background: "var(--special)" }} /><span className="rl">Birthdays</span></Link>
          {can("newsletter") && <Link to="/newsletter" aria-current={path.startsWith("/newsletter") ? "page" : undefined} title="Newsletter"><span className="dot" style={{ background: "var(--royal)" }} /><span className="rl">Newsletter</span></Link>}
        </div>
        <div className="ws-switch">
          {WS.filter((w) => can(w.module)).map((w) => (
            <span key={w.key} style={{ display: "contents" }}>
              <Link to={w.to} aria-current={workspace === w.key} title={w.label}>
                <span className="dot" style={{ background: w.color }} />
                <span className="rl">{w.label}</span>
              </Link>
              {workspace === w.key && (
                <div className="nav">
                  {nav.map((n) => (
                    <Link key={n.to} to={n.to} aria-current={isActive(n) ? "page" : undefined}>{n.label}</Link>
                  ))}
                </div>
              )}
            </span>
          ))}
        </div>
        {true && (
          <div className="ws-switch">
            <Link to={can("settings") ? "/settings" : "/settings/how"} aria-current={workspace === "settings"} title="Settings">
              <span className="dot" style={{ background: "#8d90b8" }} /><span className="rl">Settings</span>
            </Link>
            {workspace === "settings" && (
              <div className="nav">
                {nav.map((n) => <Link key={n.to} to={n.to} aria-current={isActive(n) ? "page" : undefined}>{n.label}</Link>)}
              </div>
            )}
          </div>
        )}
        <div className="rail-foot">
          {APP_ENV !== "production" && <span><span className="env-flag">{APP_ENV === "demo" ? "Demo data" : "Staging"}</span></span>}
          <Link to="/settings/profile" className="me-link" title="Edit your name">{profile?.display_name} · {profile?.role_key}</Link>
          <button onClick={() => auth.signOut()}>Sign out</button>
          <span className="small" style={{ opacity: .55 }}>Version {APP_VERSION}</span>
        </div>
      </nav>
      <main className="main" id="main"><LateBanner />{children}</main>
    </div>
  );
}

export function PageHead({ title, sub, actions, help }: { title: string; sub?: ReactNode; actions?: ReactNode; help?: string }) {
  useEffect(() => { document.title = `${title} · Royal Compass CRM`; }, [title]);
  return (
    <header className="page-head">
      <div><h1>{title}{help && <Link to={`/settings/how?s=${help}`} className="help-link" title="How this works" aria-label="How this works">?</Link>}</h1>{sub && <p>{sub}</p>}</div>
      <div className="page-head-r">{actions && <div className="toolbar" style={{ margin: 0 }}>{actions}</div>}<FoldAll /></div>
    </header>
  );
}
